import React, { useEffect, useState } from "react";
import { Modal, Typography } from "@mui/material";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { disLikeFeedBackConstants } from "../../constants";
import { BiLoaderAlt } from "react-icons/bi";
import likeDisLikeHandler from "../../common/likeDisLikeHandler";

/**
 * Closes the handler and performs some actions.
 *
 * @param {none} none - This function does not take any parameters.
 * @return {none} This function does not return any value.
 */
const FeedBackModal = ({
  isOpen,
  isChatWidget,
  closeModal,
  status,
  queryId,
  setLike,
  setDislike,
  openContactModal,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const { transcript, listening } = useSpeechRecognition();
  const [ratingText, setRatingText] = useState("");
  const maxLength = 5000;

  useEffect(() => {
    if (isChatWidget) return;
    setQuery((prevText) => transcript);
  }, [transcript]);

  /**
   * Closes the handler and performs some actions.
   *
   * @param {none} none - This function does not take any parameters.
   * @return {none} This function does not return any value.
   */
  const closeHandler = () => {
    setQuery("");
    setRatingText("");
    closeModal();
  };

  /**
   * Submits the form data to the server asynchronously.
   *
   * @return {void}
   */
  const submitHandler = async () => {
    if (query.trim() === "" && ratingText.trim() === "") {
      return;
    }

    // Get the tenantId from the URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tenantId = urlParams.get("tenantId");
    const feedback = ratingText + query;

    try {
      setIsLoading(true);
      // Create the request object with the necessary data
      const requestObj = {
        status,
        tenantId,
        queryId,
        feedback: feedback.trim(),
      };

      // Make an API call to update the like/dislike status
      const response = await likeDisLikeHandler(requestObj);
      if (response.status === "Require Attention") {
        setDislike((prevDislikes) => [...prevDislikes, queryId]);
        setLike((prevLikes) => prevLikes.filter((id) => id !== queryId));
        closeHandler();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * A function that opens the call modal.
   */
  const callHandler = () => {
    closeHandler();
    openContactModal();
  };

  return (
    <Modal open={isOpen} onClose={closeModal}>
      <div className="bg-black-100  w-full h-full z-30 ">
        <div className="flex bg-black-100  flex-col  items-center h-screen md:px-0 px-2">
          <div className="bg-white w-full max-w-118.5 my-auto py-4.5 rounded-2xl">
            <div className="flex flex-col items-start px-4">
              <div className="flex items-center justify-between w-full mb-15px">
                <div className="">
                  <img
                    className="cursor-pointer w-40 h-6"
                    alt=""
                    src="/images/logo-urc.png"
                  />
                </div>
                <div
                  onClick={callHandler}
                  className="flex items-center gap-1 border border-grey-DEDEDE rounded-3xl cursor-pointer"
                >
                  <img
                    src={"/images/call-icon.svg"}
                    alt=""
                    className="h-8 pt-1.5 pb-5px ml-2"
                  />
                  <div className="mr-11px cursor-pointer">
                    <Typography className="font-InterMedium  text-ft15.1">
                      Call
                    </Typography>
                  </div>
                </div>
              </div>
              <hr className="w-full h-1px  text-grey-DEDEDE" />
              <div className="font-InterRegular text-ft3.1 mt-33px">
                Why have you chosen this rating?
              </div>
              <div className="flex items-center gap-2 w-full mt-4.5">
                {disLikeFeedBackConstants.map((element, index) => (
                  <div
                    key={index}
                    onClick={() => setRatingText(element)}
                    className={`${
                      element === ratingText
                        ? "bg-blue-EEEFFF border-blue-20"
                        : " bg-white border-grey-DEDEDE"
                    } px-2 border py-3 rounded-lg text-black-0 cursor-pointer font-InterRegular text-ft2.1`}
                  >
                    {element}
                  </div>
                ))}
              </div>
              <div className="relative w-full h-44.5 mt-15px">
                <textarea
                  maxLength={maxLength}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-44.5 resize-none w-full outline-none  placeholder-grey-BFBFBF border border-grey-DEDEDE bg-transparent rounded-2.5 p-4 font-InterRegular text-ft15.1"
                  placeholder="Provide additional feedback"
                />
                <button onClick={SpeechRecognition.startListening}>
                  <img
                    className="absolute right-3.5 bottom-12.5px cursor-pointer"
                    alt=""
                    src={
                      listening
                        ? "/images/active-mick-icon.svg"
                        : "/images/mick-icon.svg"
                    }
                  />
                </button>
              </div>
              <div className="text-red-FF736C font-InterRegular text-ft3.1 my-3px flex justify-end w-full items-center">
                <span
                  className={`${
                    query?.length <= 4999
                      ? "text-green-6FBE0B"
                      : "text-red-FF736C"
                  }`}
                >
                  {query?.length}
                </span>
                /{maxLength}
              </div>

              <div className="flex items-center gap-4.5 w-full justify-between mt-7.75">
                <button
                  onClick={closeHandler}
                  className="w-full text-black border font-InterRegular text-ft2.1 rounded-3xl border-black-10 text-black-10 h-10 "
                >
                  Back to chat
                </button>
                <button
                  disabled={!query.length && !ratingText.length}
                  onClick={submitHandler}
                  className={`${
                    !ratingText.length && !query.length
                      ? "opacity-50"
                      : "opacity-100"
                  } w-full text-white font-InterRegular text-ft2.1 border rounded-3xl bg-blue-20 h-10 `}
                >
                  {isLoading ? (
                    <BiLoaderAlt className="w-6 mx-auto h-6 animate-spin text-white" />
                  ) : (
                    "Submit feedback"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FeedBackModal;
